import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Pencil,
  Copy,
  Check,
  RefreshCw,
  Trash2,
  Loader2,
  ExternalLink,
  Mail,
  Plus,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { PLATFORM_META, type Platform } from '../lib/platforms';
import { AccountAvatar } from '../components/AccountAvatar';
import { cn } from '../lib/cn';

type RequestStatus = 'pending' | 'partially_approved' | 'approved' | 'changes_requested' | 'rejected';
type TargetApproval = 'pending' | 'approved' | 'changes_requested' | 'rejected';

type Target = {
  targetId: string;
  kind: string;
  approvalStatus: TargetApproval;
  approvalNote: string | null;
  status: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
};

type ApprovalPost = {
  id: string;
  caption: string;
  media: Array<{ url: string; type?: string }>;
  status: string;
  scheduledAt: string | null;
  targets: Target[];
};

type Event = { postId: string | null; actor: 'owner' | 'client'; action: string; message: string | null; createdAt: string };

type ApprovalRequest = {
  id: string;
  token: string;
  title: string | null;
  message: string | null;
  status: RequestStatus;
  clientName: string | null;
  reviewedAt: string | null;
  createdAt: string;
  reviewUrl: string;
  posts: ApprovalPost[];
  events: Event[];
};

const STATUS_META: Record<RequestStatus, { label: string; icon: typeof Clock; cls: string }> = {
  pending: { label: 'Pendiente', icon: Clock, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  partially_approved: { label: 'Parcial', icon: Clock, cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  approved: { label: 'Aprobada', icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  changes_requested: { label: 'Cambios pedidos', icon: Pencil, cls: 'text-blue-700 bg-blue-50 border-blue-200' },
  rejected: { label: 'Rechazada', icon: XCircle, cls: 'text-red-700 bg-red-50 border-red-200' },
};

const TARGET_DOT: Record<TargetApproval, string> = {
  pending: 'bg-slate-300',
  approved: 'bg-emerald-500',
  changes_requested: 'bg-blue-500',
  rejected: 'bg-red-500',
};

const ACTION_LABEL: Record<string, string> = {
  requested: 'Solicitó aprobación',
  approved: 'Aprobó una red',
  rejected: 'Rechazó una red',
  changes_requested: 'Pidió cambios en una red',
  resubmitted: 'Reenvió para aprobación',
  commented: 'Comentó',
};

const FILTERS: Array<{ key: 'all' | RequestStatus; label: string }> = [
  { key: 'all', label: 'Todas' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'changes_requested', label: 'Cambios' },
  { key: 'partially_approved', label: 'Parciales' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
];

export function Approvals() {
  const { user } = useAuth();
  const tz = user?.timezone ?? 'Europe/Madrid';
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'all' | RequestStatus>('all');
  const [showReviewers, setShowReviewers] = useState(false);

  const query = useQuery({
    queryKey: ['approvals'],
    queryFn: () => api<{ approvals: ApprovalRequest[] }>('/api/approvals'),
  });

  const all = query.data?.approvals ?? [];
  const counts = all.reduce<Record<string, number>>((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {});
  const items = filter === 'all' ? all : all.filter((a) => a.status === filter);

  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <UserCheck size={22} className="text-brand-600" />
            Aprobaciones
          </h1>
          <p className="mt-2 text-slate-600">
            Comparte un enlace con tu cliente para que revise y apruebe cada red antes de que se publique.
          </p>
        </div>
        <button onClick={() => setShowReviewers(true)} className="btn-secondary gap-2">
          <Mail size={14} /> Revisores
        </button>
      </div>

      {showReviewers && <ReviewersModal onClose={() => setShowReviewers(false)} />}

      <div className="mt-6 flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={cn(
              'px-3 py-1.5 rounded-lg text-sm font-medium transition border',
              filter === f.key ? 'bg-brand-600 text-white border-brand-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
            )}
          >
            {f.label}{f.key !== 'all' && counts[f.key] ? ` (${counts[f.key]})` : ''}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <div className="mt-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={26} /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 card text-center py-14">
          <UserCheck size={36} className="mx-auto text-slate-300" />
          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            {filter === 'all' ? 'Aún no has pedido ninguna aprobación' : 'No hay solicitudes en este estado'}
          </h2>
          <p className="mt-1.5 text-slate-500 max-w-md mx-auto">
            Crea una publicación y pulsa <strong>"Pedir aprobación"</strong> en el editor para generar un enlace de revisión.
          </p>
          <Link to="/dashboard/posts/new" className="btn-primary gap-2 mt-5 inline-flex">
            Crear publicación
          </Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((r) => <RequestCard key={r.id} request={r} tz={tz} queryClient={queryClient} />)}
        </div>
      )}
    </>
  );
}

function RequestCard({ request, tz, queryClient }: { request: ApprovalRequest; tz: string; queryClient: ReturnType<typeof useQueryClient> }) {
  const [copied, setCopied] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const meta = STATUS_META[request.status];
  const Icon = meta.icon;
  const reviewUrl = `${window.location.origin}/approve/${request.token}`;
  const fmt = (d: string) => new Date(d).toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short', timeZone: tz });

  const resubmit = useMutation({
    mutationFn: () => api(`/api/approvals/${request.id}/resubmit`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['approvals'] }),
  });
  const cancel = useMutation({
    mutationFn: () => api(`/api/approvals/${request.id}/cancel`, { method: 'POST' }),
    onSuccess: () => {
      setConfirmCancel(false);
      queryClient.invalidateQueries({ queryKey: ['approvals'] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(reviewUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  }

  const canResubmit = request.status === 'changes_requested' || request.status === 'rejected' || request.status === 'partially_approved';
  const canShare = request.status !== 'approved';

  return (
    <div className="card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border', meta.cls)}>
          <Icon size={13} /> {meta.label}
        </span>
        <h3 className="text-sm font-semibold text-slate-900">
          {request.title?.trim() || (request.posts.length === 1 ? '1 publicación' : `${request.posts.length} publicaciones`)}
        </h3>
        <span className="text-xs text-slate-400">· creada {fmt(request.createdAt)}</span>
        {request.clientName && <span className="text-xs text-slate-400">· por {request.clientName}</span>}
      </div>

      <div className="mt-3 space-y-3">
        {request.posts.map((post) => <PostRow key={post.id} post={post} />)}
      </div>

      {request.events.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowHistory((s) => !s)} className="text-xs font-medium text-slate-500 hover:text-slate-700">
            {showHistory ? 'Ocultar historial' : `Ver historial (${request.events.length})`}
          </button>
          {showHistory && (
            <ol className="mt-2 space-y-1.5 border-l-2 border-slate-100 pl-3">
              {request.events.map((e, i) => (
                <li key={i} className="text-xs text-slate-600">
                  <span className="font-medium">{e.actor === 'owner' ? 'Tú' : (request.clientName?.trim() || 'Cliente')}</span>
                  {': '}{ACTION_LABEL[e.action] ?? e.action}
                  <span className="text-slate-400"> · {fmt(e.createdAt)}</span>
                  {e.message && <p className="mt-0.5 text-slate-500 italic whitespace-pre-wrap">"{e.message}"</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
        {canShare && (
          <>
            <button onClick={copyLink} className="btn-secondary gap-2 text-sm">
              {copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copiado' : 'Copiar enlace'}
            </button>
            <a href={reviewUrl} target="_blank" rel="noreferrer" className="btn-secondary gap-2 text-sm">
              <ExternalLink size={14} /> Abrir
            </a>
          </>
        )}
        {canResubmit && (
          <button onClick={() => resubmit.mutate()} disabled={resubmit.isPending} className="btn-primary gap-2 text-sm">
            {resubmit.isPending ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} Reenviar
          </button>
        )}
        {request.status !== 'approved' && (
          confirmCancel ? (
            <span className="inline-flex items-center gap-2 text-sm ml-auto">
              <span className="text-slate-500">¿Cancelar la solicitud? Los posts sin publicar se cancelarán.</span>
              <button onClick={() => cancel.mutate()} disabled={cancel.isPending} className="text-red-600 font-medium hover:text-red-700">Sí</button>
              <button onClick={() => setConfirmCancel(false)} className="text-slate-500 hover:text-slate-700">No</button>
            </span>
          ) : (
            <button onClick={() => setConfirmCancel(true)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-600 transition">
              <Trash2 size={14} /> Cancelar
            </button>
          )
        )}
      </div>
    </div>
  );
}

function PostRow({ post }: { post: ApprovalPost }) {
  const thumb = post.media?.[0];
  return (
    <div className="flex gap-3 rounded-xl border border-slate-100 p-2.5">
      {thumb && (
        thumb.type === 'video'
          ? <video src={thumb.url} className="w-14 h-14 rounded-lg object-cover bg-slate-100 shrink-0" />
          : <img src={thumb.url} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm text-slate-700 line-clamp-2 whitespace-pre-wrap">
          {post.caption || <span className="italic text-slate-400">Sin texto (solo media)</span>}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {post.targets.map((t) => {
            const meta = PLATFORM_META[t.platform];
            const Icon = meta.icon;
            return (
              <span
                key={t.targetId}
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md border border-slate-200 bg-white"
                title={`${meta.label}: ${t.approvalStatus}${t.approvalNote ? ` — ${t.approvalNote}` : ''}`}
              >
                <Icon size={11} color={meta.color} />
                <span className={cn('h-1.5 w-1.5 rounded-full', TARGET_DOT[t.approvalStatus])} />
                {t.status === 'published' && <CheckCircle2 size={11} className="text-emerald-500" />}
              </span>
            );
          })}
        </div>
        {/* Per-network client notes */}
        {post.targets.filter((t) => t.approvalNote).map((t) => (
          <p key={t.targetId} className="mt-1 text-xs text-slate-500">
            <span className="font-medium">{PLATFORM_META[t.platform].label}:</span> {t.approvalNote}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ── Reviewers management (account → reviewer emails) ─────── */
type ReviewerAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  profilePhotoUrl: string | null;
  reviewers: Array<{ id: string; email: string; name: string | null }>;
};

function ReviewersModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['reviewers'],
    queryFn: () => api<{ accounts: ReviewerAccount[] }>('/api/reviewers'),
  });
  const accounts = query.data?.accounts ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Revisores por cuenta</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Cuando envíes a aprobar una publicación, sus revisores recibirán el enlace por email automáticamente.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {query.isLoading ? (
            <div className="py-10 flex justify-center text-slate-400"><Loader2 className="animate-spin" size={22} /></div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No tienes cuentas conectadas.</p>
          ) : (
            <div className="space-y-3">
              {accounts.map((a) => (
                <ReviewerAccountRow key={a.id} account={a} queryClient={queryClient} />
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 text-right">
          <button onClick={onClose} className="btn-secondary">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function ReviewerAccountRow({
  account,
  queryClient,
}: {
  account: ReviewerAccount;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const meta = PLATFORM_META[account.platform];

  const add = useMutation({
    mutationFn: () =>
      api('/api/reviewers', {
        method: 'POST',
        body: JSON.stringify({ accountId: account.id, email: email.trim(), name: name.trim() || null }),
      }),
    onSuccess: () => {
      setEmail('');
      setName('');
      queryClient.invalidateQueries({ queryKey: ['reviewers'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/reviewers/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reviewers'] }),
  });

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2.5 mb-2">
        <AccountAvatar account={account} size={30} />
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{account.displayName ?? account.username ?? meta.label}</div>
          <div className="text-xs text-slate-500">{meta.label}</div>
        </div>
      </div>

      {account.reviewers.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {account.reviewers.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 border border-slate-200 rounded-full pl-2.5 pr-1 py-1">
              <span className="text-slate-700">{r.name ? `${r.name} · ${r.email}` : r.email}</span>
              <button
                onClick={() => remove.mutate(r.id)}
                disabled={remove.isPending}
                className="text-slate-400 hover:text-red-600 rounded-full"
                title="Quitar revisor"
              >
                <X size={13} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="email"
          className="input flex-1 min-w-[180px] text-sm"
          placeholder="email@cliente.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && emailValid) add.mutate(); }}
        />
        <input
          className="input w-32 text-sm"
          placeholder="Nombre (opc.)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && emailValid) add.mutate(); }}
        />
        <button
          onClick={() => add.mutate()}
          disabled={!emailValid || add.isPending}
          className="btn-secondary gap-1.5 text-sm shrink-0"
        >
          {add.isPending ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Añadir
        </button>
      </div>
    </div>
  );
}
